/**
 * [Iter 420 — 2026-03-01] ProjectJobCardsCard
 *
 * Same pattern as ProjectPickingCard: a tab-bar of every job card on
 * this project (direct + family-tree via picking) with the active
 * one's full workflow (phase, checklist, technician, progress) shown
 * inline. Users don't leave the project screen to inspect a JC — they
 * can see task status, comments, photos count, and hit "Open full →"
 * only when they need to edit.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, ExternalLink, RefreshCw, MessageSquare,
  CheckCircle2, Circle, Loader2, User as UserIcon, Clock,
} from "lucide-react";
import api from "../../lib/api";

const PHASE_COLOR = {
  open: "text-emerald-300 bg-emerald-500/15",
  in_progress: "text-cyan-300 bg-cyan-500/15",
  awaiting_signoff: "text-amber-300 bg-amber-500/15",
  closed: "text-white bg-white/10",
  cancelled: "text-rose-300 bg-rose-500/15",
};

export default function ProjectJobCardsCard({ projectId }) {
  const [rows, setRows] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobcards", {
        params: { project_id: projectId, limit: 200 },
      });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setRows(items);
      if (items.length && !activeId) setActiveId(items[0].id);
      else if (!items.length) setActiveId(null);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadDetail = useCallback(async (jid) => {
    if (!jid) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/jobcards/${jid}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadDetail(activeId); }, [activeId, loadDetail]);

  if (loading) {
    return (
      <div className="hv-card !p-3 flex items-center gap-2 text-[11px] text-[#647087]" data-testid="proj-jobcards-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading job cards…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="hv-card !p-3 text-center" data-testid="proj-jobcards-empty">
        <ClipboardList className="w-6 h-6 mx-auto text-[#647087] mb-1.5" />
        <div className="text-[11px] text-[#647087] italic">
          No job cards linked to this project yet.
        </div>
        <Link
          to={`/jobcards/new?project_id=${projectId}`}
          className="hv-btn hv-btn-primary text-[11px] mt-2 inline-flex"
          data-testid="proj-jobcards-new-empty"
        >
          <ClipboardList className="w-3 h-3" /> Create job card
        </Link>
      </div>
    );
  }

  const activeJC = detail;
  const checklist = Array.isArray(activeJC?.checklist) ? activeJC.checklist : [];
  // [Iter 420 — progress calc fix]
  // Rejected tasks are EXCLUDED from the denominator — a rejected task
  // is not "incomplete work outstanding", it's work explicitly taken
  // off the scope. So the progress bar reflects how much of the
  // *accepted* scope is done. Rejected count is shown separately as a
  // secondary chip. Example (7 tasks total, 6 done, 1 rejected):
  //   done=6, rejected=1, denom=(7-1)=6, complete=6/6=100%, rejected 1/7=14%.
  const doneCount = checklist.filter((c) => (c?.status || "").toLowerCase() === "done").length;
  const rejectedCount = checklist.filter((c) => (c?.status || "").toLowerCase() === "rejected").length;
  const totalCount = checklist.length;
  const acceptedTotal = Math.max(totalCount - rejectedCount, 0);
  const pct = acceptedTotal > 0 ? Math.round((doneCount / acceptedTotal) * 100) : 0;
  const rejectedPct = totalCount > 0 ? Math.round((rejectedCount / totalCount) * 100) : 0;
  const phaseCls = PHASE_COLOR[activeJC?.phase] || "text-[#a0aabf] bg-white/[0.06]";

  return (
    <div className="hv-card !p-3" data-testid="proj-jobcards-card">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="font-display font-semibold text-sm text-[#1FB6C1] inline-flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" /> Job Cards
          <span className="text-[10px] text-[#647087] font-mono">· {rows.length} card{rows.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => { loadRows(); loadDetail(activeId); }}
            className="text-[10px] text-[#647087] hover:text-white inline-flex items-center gap-1"
            data-testid="proj-jobcards-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <Link
            to={`/jobcards/new?project_id=${projectId}`}
            className="hv-btn hv-btn-ghost text-[11px]"
            data-testid="proj-jobcards-new"
          >
            + New JC
          </Link>
        </div>
      </div>

      {/* Tab bar of job cards */}
      <div className="flex overflow-x-auto gap-1 mb-3 -mx-1 px-1 hv-scrollbar-thin">
        {rows.map((jc) => {
          const label = jc.short_id || jc.title || jc.id.slice(0, 8);
          const via = jc.linked_via;
          return (
            <button
              key={jc.id}
              type="button"
              onClick={() => setActiveId(jc.id)}
              className={`shrink-0 px-2.5 py-1 rounded text-[11px] border ${
                activeId === jc.id
                  ? "bg-[#1FB6C1]/20 border-[#1FB6C1] text-white"
                  : "bg-white/[0.02] border-white/5 text-[#a0aabf] hover:bg-white/[0.04]"
              }`}
              data-testid={`proj-jc-tab-${jc.id}`}
            >
              {label}
              {jc.phase && <span className="ml-1 text-[9px] text-[#647087]">· {jc.phase}</span>}
              {via && via.kind && via.kind !== "direct" && (
                <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA]" title={`Linked via ${via.kind}`}>
                  via {via.kind === "picking_list" ? "picking" : via.kind}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active JC detail */}
      {loadingDetail && (
        <div className="flex items-center gap-2 text-[11px] text-[#647087]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}

      {!loadingDetail && activeJC && (
        <div className="space-y-3">
          {/* Sub-header */}
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-white truncate">{activeJC.title || activeJC.short_id}</div>
              {activeJC.description && (
                <div
                  className="text-[11px] text-[#a0aabf] mt-1 whitespace-pre-wrap"
                  data-testid="proj-jc-description"
                >
                  {activeJC.description}
                </div>
              )}
              <div className="text-[10px] text-[#647087] flex items-center gap-2 flex-wrap mt-0.5">
                {activeJC.customer_name && <span>{activeJC.customer_name}</span>}
                {activeJC.technician_name && (
                  <span className="inline-flex items-center gap-0.5">
                    <UserIcon className="w-2.5 h-2.5" /> {activeJC.technician_name}
                  </span>
                )}
                {activeJC.scheduled_at && (
                  <span className="inline-flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" /> {String(activeJC.scheduled_at).slice(0, 16).replace("T", " ")}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${phaseCls}`} data-testid="proj-jc-phase">
                {activeJC.phase || "—"}
              </span>
              <Link
                to={`/jobcards/${activeJC.id}`}
                className="hv-btn hv-btn-primary text-[11px]"
                data-testid={`proj-jc-open-full-${activeJC.id}`}
              >
                <ExternalLink className="w-3 h-3" /> Open full
              </Link>
            </div>
          </div>

          {/* Progress bar — accepted-scope only (rejected excluded) */}
          {totalCount > 0 && (
            <div>
              <div className="flex items-center justify-between text-[10px] text-[#647087] mb-1 gap-2 flex-wrap">
                <span className="uppercase tracking-wider">
                  Progress · {doneCount}/{acceptedTotal} accepted task{acceptedTotal === 1 ? "" : "s"}
                </span>
                <div className="flex items-center gap-2">
                  {rejectedCount > 0 && (
                    <span className="font-mono text-rose-300" data-testid="proj-jc-rejected-chip">
                      {rejectedCount} rejected · {rejectedPct}%
                    </span>
                  )}
                  <span className="font-mono text-[#1FB6C1]">{pct}%</span>
                </div>
              </div>
              <div className="h-1.5 rounded bg-white/[0.06] overflow-hidden flex" data-testid="proj-jc-progress-bar">
                <div
                  className="h-full bg-gradient-to-r from-[#1FB6C1] to-[#10B981]"
                  style={{ width: `${(doneCount / Math.max(totalCount, 1)) * 100}%` }}
                />
                {rejectedCount > 0 && (
                  <div
                    className="h-full bg-rose-500/60"
                    style={{ width: `${(rejectedCount / Math.max(totalCount, 1)) * 100}%` }}
                    title={`${rejectedCount} rejected`}
                  />
                )}
              </div>
            </div>
          )}

          {/* Checklist inline — full detail per task */}
          {totalCount > 0 && (
            <div className="space-y-1.5" data-testid="proj-jc-checklist">
              {checklist.map((task, idx) => {
                const status = (task?.status || "").toLowerCase();
                const isDone = status === "done";
                const isRejected = status === "rejected";
                const comments = Array.isArray(task?.comments) ? task.comments : [];
                const photos = Array.isArray(task?.photos) ? task.photos : [];
                const legacyComment = (task?.comment || "").trim();
                const assignees = Array.isArray(task?.assignee_names) ? task.assignee_names.filter(Boolean) : [];
                const label = task?.label || task?.text || task?.title || "Task";
                return (
                  <div
                    key={idx}
                    className={`rounded p-2 ${isDone ? "bg-emerald-500/[0.04] border border-emerald-500/10" : isRejected ? "bg-rose-500/[0.04] border border-rose-500/10" : "bg-white/[0.02] border border-white/5"}`}
                    data-testid={`proj-jc-task-${idx}`}
                  >
                    {/* Task header row */}
                    <div className="flex items-start gap-2 text-[11px]">
                      {isDone ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                      ) : isRejected ? (
                        <span className="text-rose-400 shrink-0 w-3.5 h-3.5 inline-flex items-center justify-center mt-0.5">✗</span>
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-[#647087] shrink-0 mt-0.5" />
                      )}
                      <span className="text-[9px] text-[#647087] font-mono mt-0.5 shrink-0">{idx + 1}.</span>
                      <span className={`flex-1 ${isDone ? "text-white" : isRejected ? "text-rose-200" : "text-[#a0aabf]"}`}>
                        {label}
                      </span>
                      <span
                        className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                          isDone ? "bg-emerald-500/15 text-emerald-300"
                          : isRejected ? "bg-rose-500/15 text-rose-300"
                          : "bg-white/[0.06] text-[#a0aabf]"
                        }`}
                        data-testid={`proj-jc-task-${idx}-status`}
                      >
                        {status || "open"}
                      </span>
                    </div>

                    {/* Assignees */}
                    {assignees.length > 0 && (
                      <div className="mt-1.5 ml-6 flex flex-wrap gap-1" data-testid={`proj-jc-task-${idx}-assignees`}>
                        {assignees.map((nm, ai) => (
                          <span key={ai} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-200 inline-flex items-center gap-0.5">
                            <UserIcon className="w-2.5 h-2.5" /> {nm}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Legacy single-line comment (older jobcards) */}
                    {legacyComment && (
                      <div
                        className="mt-1.5 ml-6 text-[11px] text-[#a0aabf] whitespace-pre-wrap"
                        data-testid={`proj-jc-task-${idx}-legacy-comment`}
                      >
                        {legacyComment}
                      </div>
                    )}

                    {/* Comments — full text, each with author + timestamp */}
                    {comments.length > 0 && (
                      <div className="mt-2 ml-6 space-y-1" data-testid={`proj-jc-task-${idx}-comments`}>
                        {comments.map((cm, ci) => (
                          <div key={ci} className="text-[11px] flex items-start gap-1.5">
                            <MessageSquare className="w-2.5 h-2.5 text-[#647087] shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className={`whitespace-pre-wrap ${isRejected ? "text-rose-200" : "text-[#a0aabf]"}`}>
                                {cm?.text || cm?.body || ""}
                              </div>
                              <div className="text-[9px] text-[#647087] mt-0.5">
                                {cm?.author_name || cm?.author || cm?.user_name || "—"}
                                {cm?.at && <span> · {String(cm.at).slice(0, 16).replace("T", " ")}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Photo thumbnails */}
                    {photos.length > 0 && (
                      <div className="mt-2 ml-6 flex flex-wrap gap-1" data-testid={`proj-jc-task-${idx}-photos`}>
                        {photos.slice(0, 8).map((ph, pi) => {
                          const src = ph?.thumb_url || ph?.url || ph?.data_url || ph;
                          if (!src || typeof src !== "string") return null;
                          return (
                            <a
                              key={pi}
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-12 h-12 rounded overflow-hidden bg-white/[0.06] hover:ring-2 hover:ring-[#1FB6C1]"
                              data-testid={`proj-jc-task-${idx}-photo-${pi}`}
                            >
                              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </a>
                          );
                        })}
                        {photos.length > 8 && (
                          <span className="text-[10px] text-[#647087] self-center ml-1">+{photos.length - 8} more</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {totalCount === 0 && (
            <div className="text-[11px] text-[#647087] italic">This job card has no checklist items yet.</div>
          )}
        </div>
      )}
    </div>
  );
}
